import {
	StylovyzeDialog,
	decoratedAttributes,
	useGlobalization,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import {
	DialogProps,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	selectRehabTreesIsCreating,
	selectRehabTreesList,
	selectRehabTreesNameError,
	selectAssetFilters,
} from '@Selectors';
import {
	addRehabTree,
	setNewTreeName,
	setRehabTreeNameError,
} from '@Actions/index';
import {
	useEnabledAssetTypes,
	useEnabledInspectionStandards,
	useEnabledSystemTypes,
} from './useEnabledAssetTypes';
import { SystemType } from '@innovyze/lib_asset_schema';
import { useAssetSchema } from '../../hooks/assetSchemaHooks';
import { getNewAssetIds } from '@Utils/assetSchemaUtils';
interface Props {
	open: boolean;
	onClose: () => void;
}

export const NewRehab = ({ open, onClose }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();

	const existingTrees = selectRehabTreesList();
	const assetFilters = useSelector(selectAssetFilters);
	const [newName, setNewName] = useState('');
	const [newTypeId, setNewTypeId] = useState<string | undefined>(undefined);
	const [newSystemType, setNewSystemType] = useState<SystemType | undefined>(
		undefined,
	);
	const [inspectionStandard, setInspectionStandard] = useState<
		string | undefined
	>(undefined);
	const [assetFilterId, setAssetFilterId] = useState<string | undefined>(
		undefined,
	);

	const { enabledSystemTypes, pickerStatus } = useEnabledSystemTypes();
	const { enabledAssetTypes } = useEnabledAssetTypes(newSystemType);
	// The pickers list summaries; the full schema of the chosen type supplies
	// its inspection standards and canonical ids. It is undefined until loaded,
	// so the create button stays disabled meanwhile.
	const { data: newType, status: schemaStatus } = useAssetSchema(
		newSystemType?.id ?? '',
		newTypeId ?? '',
	);
	const isSchemaLoading =
		!!newSystemType && !!newTypeId && schemaStatus === 'pending';
	const { enabledInspectionStandards } =
		useEnabledInspectionStandards(newType);

	const nameError = selectRehabTreesNameError();
	const isCreating = selectRehabTreesIsCreating();

	const handleCancelNewModel = () => {
		onClose();
	};
	const shouldShowAssetsFilter = !!useIsFeatureEnabled(
		'info-360-assets-filter-rehab',
	);

	useEffect(() => {
		setNewName('');
		dispatch(setRehabTreeNameError(t('Name is required')));
		setNewSystemType(undefined);
		setNewTypeId(undefined);
		setInspectionStandard(undefined);
	}, [open]);

	const handleCreateNewModel = () => {
		if (
			nameError.length === 0 &&
			newSystemType &&
			newType &&
			(enabledInspectionStandards.length === 0 || inspectionStandard)
		) {
			if (newName.trim().length === 0) {
				throw new Error('Name is required');
			}

			const { systemTypeId, assetTypeId } = getNewAssetIds({
				assetSchema: newType,
			});

			dispatch(
				setNewTreeName({
					name: newName.trim(),
					systemType: systemTypeId,
					assetType: assetTypeId,
					assetFilterId,
					treeNodes: [],
					inspectionStandard,
				}),
			);
			dispatch(
				addRehabTree({
					history: history,
					assetFilterId,
				}),
			);
		}
	};

	const handlelNameChangeValue = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		let error = '';
		if (event.target && event.target.value !== '') {
			setNewName(event.target.value);
			const duplicateName = existingTrees.filter(
				tree =>
					tree.name.toUpperCase().trim() ===
					event.target.value.toUpperCase().trim(),
			);

			if (duplicateName.length > 0) {
				error = t('Enter a unique name');
			} else {
				const rexp = new RegExp('^[a-zA-Z0-9 ]+$');
				const test = rexp.test(event.target.value);
				if (!test) {
					error = t('Only alphanumeric characters are allowed');
				} else {
					const rexp2 = new RegExp('^.{1,50}$');
					const test2 = rexp2.test(event.target.value);
					if (!test2) {
						error = t(
							'Name cannot contain more than 50 characters',
						);
					}
				}
			}
		} else {
			setNewName('');
			error = t('Name is required');
		}
		dispatch(setRehabTreeNameError(error));
	};

	const handleSystemTypeSelectChange = (event: SelectChangeEvent<string>) => {
		const selectedType = event?.target.value;

		if (selectedType === '') {
			setNewSystemType(undefined);
		} else {
			const systemType = enabledSystemTypes.find(
				type => type.id === selectedType,
			);
			setNewSystemType(systemType);
		}

		setNewTypeId(undefined);
		setInspectionStandard(undefined);
	};

	const handleAssetTypeSelectChange = (event: SelectChangeEvent<string>) => {
		const selectedType = event?.target.value;
		if (selectedType === '') {
			setNewTypeId(undefined);
		} else {
			setNewTypeId(selectedType);
		}

		setInspectionStandard(undefined);
	};

	const handleInspectionStandardSelectChange = (
		event: SelectChangeEvent<string>,
	) => {
		const selectedStandard = event?.target.value;

		setInspectionStandard(selectedStandard);
	};

	const handleAssetFilterSelectChange = (
		event: SelectChangeEvent<string>,
	) => {
		const selectedFilter = event?.target.value;
		setAssetFilterId(selectedFilter);
	};

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: open,
		maxWidth: 'md',
		onClose: onCloseDialog,
	};

	// The pickers are empty while the catalogue loads and when it fails or the
	// release flag is closed, so say which it is instead of showing blank lists.
	const pickerMessage = (() => {
		if (pickerStatus === 'loading') {
			return t('Loading system types');
		}
		if (pickerStatus === 'error') {
			return t('Unable to load system types. Try again later.');
		}
		if (pickerStatus === 'empty' || pickerStatus === 'noCapable') {
			return t('No system types are available');
		}
		return undefined;
	})();
	const schemaMessage = (() => {
		if (schemaStatus === 'error' && newSystemType && newTypeId) {
			return t('Unable to load this asset type. Try again later.');
		}
		if (isSchemaLoading) {
			return t('Loading asset type');
		}
		return undefined;
	})();

	const isCreateButtonDisabled = () =>
		isCreating ||
		nameError.length !== 0 ||
		!newSystemType ||
		!newType ||
		(enabledInspectionStandards.length !== 0 && !inspectionStandard);

	const adpAttrs = decoratedAttributes({
		systemtype: newSystemType?.displayName ?? '',
		inspectionstandard: inspectionStandard ?? '',
		name: newName,
		type: newType?.displayName ?? '',
		disablestandardtypes: (
			enabledInspectionStandards.length === 0
		).toString(),
	});

	return (
		<StylovyzeDialog
			dialogProps={dialogProps}
			data-cy="new-dialog"
			title={t('New Decision Tree')}
			open={open}
			cancelText={t('Cancel')}
			cancelButtonProps={{
				disabled: isCreating,
				dataCy: 'create-cancel',
			}}
			onCancel={handleCancelNewModel}
			confirmText={t('Create Decision Tree')}
			confirmButtonProps={{
				disabled: isCreateButtonDisabled(),
				dataCy: 'create-confirm',
				'adp-click': 'true',
				id: 'create-decision-tree',
				type: 'button',
				name: 'Create Decision Tree',
				...adpAttrs,
			}}
			onConfirm={handleCreateNewModel}>
			<Stack style={{ width: '400px' }}>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						error={nameError.length !== 0}
						required
						name="name"
						label={t('Enter Name')}
						variant="outlined"
						onChange={handlelNameChangeValue}
						helperText={nameError.length !== 0 ? nameError : ' '}
					/>
				</FormControl>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel
						id="system-type-select-label"
						variant="outlined"
						error={!newSystemType}>
						{t('System Type')}
					</InputLabel>
					<Select
						labelId="system-type-select-label"
						data-cy="systemTypeField"
						name="systemType"
						error={!newSystemType}
						onChange={handleSystemTypeSelectChange}
						label={t('System Type')}
						value={newSystemType?.id}>
						{enabledSystemTypes.map((systemType, index) => {
							return (
								<MenuItem value={systemType.id} key={index}>
									{t(systemType.displayName)}
								</MenuItem>
							);
						})}
					</Select>
					<FormHelperText error>
						{pickerMessage ??
							(newSystemType ? ' ' : t('Select a System Type'))}
					</FormHelperText>
				</FormControl>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel
						variant="outlined"
						error={newSystemType && !newTypeId}>
						{t('Asset Type')}
					</InputLabel>
					<Select
						data-cy="assetTypeField"
						name="assetType"
						value={newTypeId ?? ''}
						error={newSystemType && !newTypeId}
						onChange={handleAssetTypeSelectChange}
						label={t('Asset Type')}>
						{enabledAssetTypes.map((assetType, index) => {
							return (
								<MenuItem value={assetType.id} key={index}>
									{t(assetType.displayName)}
								</MenuItem>
							);
						})}
					</Select>
					<FormHelperText error>
						{schemaMessage ??
							(newSystemType && !newTypeId
								? t('Select an asset type')
								: ' ')}
					</FormHelperText>
				</FormControl>
				{shouldShowAssetsFilter && (
					<FormControl variant="outlined" margin="dense" fullWidth>
						<InputLabel>{t('Asset Filter')}</InputLabel>
						<Select
							name="assetFilter"
							onChange={handleAssetFilterSelectChange}
							label={t('Asset Filter')}
							disabled={
								!assetFilters || assetFilters.length === 0
							}>
							{assetFilters &&
								assetFilters.map(f => (
									<MenuItem value={f._id} key={f._id}>
										{f.name}
									</MenuItem>
								))}
						</Select>
						<FormHelperText> </FormHelperText>
					</FormControl>
				)}
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel
						id="inspection-standard-select-label"
						variant="outlined"
						error={
							enabledInspectionStandards.length !== 0 &&
							!inspectionStandard
						}>
						{t('Inspection Standard')}
					</InputLabel>
					<Select
						data-cy="inspectionStandardField"
						disabled={enabledInspectionStandards.length === 0}
						name="inspectionStandard"
						error={
							enabledInspectionStandards.length !== 0 &&
							!inspectionStandard
						}
						onChange={handleInspectionStandardSelectChange}
						label={t('Inspection Standard')}
						value={inspectionStandard}>
						{enabledInspectionStandards.map(
							(inspectionStandard, index) => {
								return (
									<MenuItem
										value={inspectionStandard}
										key={index}>
										{t(inspectionStandard)}
									</MenuItem>
								);
							},
						)}
					</Select>
					<FormHelperText error>
						{enabledInspectionStandards.length !== 0 &&
						!inspectionStandard
							? t('Select an Inspection Standard')
							: ' '}
					</FormHelperText>
				</FormControl>
			</Stack>
		</StylovyzeDialog>
	);
};
